/**
 * SEO Meta Tags Utility
 *
 * Quick Win for FRONTEND-006: AIALGO Frontend Metrics Optimization
 * Priority: High | Estimated Effort: 6h
 *
 * Provides utilities for generating optimized meta tags for social media,
 * search engines, and Open Graph protocols
 */

export interface MetaTags {
  title: string
  description: string
  keywords?: string
  canonical?: string
  ogType?: 'website' | 'article' | 'product'
  ogImage?: string
  ogImageAlt?: string
  ogImageWidth?: number
  ogImageHeight?: number
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player'
  noindex?: boolean
  nofollow?: boolean
  structuredData?: Record<string, any>
  alternateLocale?: Array<{ locale: string; url: string }>
}

export interface PageMetaOptions {
  title: string
  description: string
  keywords?: string[]
  image?: string
  imageAlt?: string
  canonical?: string
  noindex?: boolean
  nofollow?: boolean
  type?: 'website' | 'article' | 'product'
  publishedTime?: string
  modifiedTime?: string
  author?: string
  section?: string
  tags?: string[]
}

const SITE_CONFIG = {
  name: 'AIAlgo.trading',
  url: 'https://aialgo.trading',
  defaultTitle: 'AIAlgo.trading - Master Algorithmic Trading',
  defaultDescription: 'Learn algorithmic trading from industry experts. Comprehensive education, tools, and consulting services for automated trading strategies.',
  defaultImage: '/og-image-default.jpg',
  twitterHandle: '@aialgo_trading',
  locale: 'en_US',
} as const

/**
 * Generate full page title with site name suffix
 */
export function generatePageTitle(title: string): string {
  const maxLength = 60 // Google truncates titles > 60 chars
  const fullTitle = title.includes(SITE_CONFIG.name)
    ? title
    : `${title} | ${SITE_CONFIG.name}`

  if (fullTitle.length <= maxLength) {
    return fullTitle
  }

  // Truncate while preserving site name
  const availableLength = maxLength - SITE_CONFIG.name.length - 4 // 4 for " | "
  return `${title.substring(0, availableLength - 3)}... | ${SITE_CONFIG.name}`
}

/**
 * Generate optimized meta description
 */
export function generateMetaDescription(description: string): string {
  const maxLength = 160 // Google truncates descriptions > 160 chars
  const cleaned = description.trim().replace(/\s+/g, ' ')

  if (cleaned.length <= maxLength) {
    return cleaned
  }

  return cleaned.substring(0, maxLength - 3).trim() + '...'
}

/**
 * Generate meta keywords string
 */
export function generateKeywords(keywords: string[]): string {
  const maxKeywords = 10 // Don't stuff too many keywords
  return keywords.slice(0, maxKeywords).join(', ')
}

/**
 * Generate canonical URL
 */
export function generateCanonical(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `${SITE_CONFIG.url}${cleanPath}`
}

/**
 * Generate Open Graph meta tags
 */
export function generateOpenGraphTags(options: PageMetaOptions): MetaTags {
  const title = generatePageTitle(options.title)
  const description = generateMetaDescription(options.description)
  const image = options.image || SITE_CONFIG.defaultImage

  return {
    title,
    description,
    ogType: options.type || 'website',
    ogImage: image.startsWith('http') ? image : `${SITE_CONFIG.url}${image}`,
    ogImageAlt: options.imageAlt,
    canonical: options.canonical,
  }
}

/**
 * Generate Twitter Card meta tags
 */
export function generateTwitterCardTags(options: PageMetaOptions): MetaTags {
  const image = options.image || SITE_CONFIG.defaultImage
  const cardType = image.includes('large') || image.includes('1200')
    ? 'summary_large_image'
    : 'summary'

  return {
    title: generatePageTitle(options.title),
    description: generateMetaDescription(options.description),
    twitterCard: cardType,
    ogImage: image.startsWith('http') ? image : `${SITE_CONFIG.url}${image}`,
    ogImageAlt: options.imageAlt,
  }
}

/**
 * Generate JSON-LD structured data for Article
 */
export function generateArticleStructuredData(options: PageMetaOptions & {
  url: string
  imageUrl?: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: options.title,
    description: options.description,
    image: options.imageUrl || `${SITE_CONFIG.url}${options.image || SITE_CONFIG.defaultImage}`,
    author: {
      '@type': 'Organization',
      name: options.author || SITE_CONFIG.name,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_CONFIG.name,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_CONFIG.url}/logo.png`,
      },
    },
    datePublished: options.publishedTime,
    dateModified: options.modifiedTime,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': options.url,
    },
  }
}

/**
 * Generate JSON-LD structured data for Product (Algorithm)
 */
export function generateProductStructuredData(options: {
  name: string
  description: string
  image: string
  sku: string
  price?: number
  priceCurrency?: string
  availability?: 'InStock' | 'OutOfStock'
  rating?: number
  reviewCount?: number
  aggregateRating?: {
    ratingValue: number
    reviewCount: number
  }
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: options.name,
    description: options.description,
    image: options.image,
    sku: options.sku,
    offers: options.price ? {
      '@type': 'Offer',
      price: options.price,
      priceCurrency: options.priceCurrency || 'USD',
      availability: `https://schema.org/${options.availability || 'InStock'}`,
    } : undefined,
    aggregateRating: options.aggregateRating ? {
      '@type': 'AggregateRating',
      ratingValue: options.aggregateRating.ratingValue,
      reviewCount: options.aggregateRating.reviewCount,
    } : undefined,
  }
}

/**
 * Generate JSON-LD structured data for Organization
 */
export function generateOrganizationStructuredData() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_CONFIG.name,
    url: SITE_CONFIG.url,
    logo: `${SITE_CONFIG.url}/logo.png`,
    sameAs: [
      'https://twitter.com/aialgo_trading',
      'https://github.com/mkc909',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@aialgo.trading',
    },
  }
}

/**
 * Generate JSON-LD structured data for BreadcrumbList
 */
export function generateBreadcrumbStructuredData(items: Array<{
  name: string
  url: string
}>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}

/**
 * Generate comprehensive meta tags for a page
 */
export function generatePageMetaTags(options: PageMetaOptions & {
  url?: string
  imageUrl?: string
}): MetaTags {
  const canonical = options.canonical || (options.url ? generateCanonical(options.url) : undefined)
  const keywords = options.keywords ? generateKeywords(options.keywords) : undefined

  return {
    title: generatePageTitle(options.title),
    description: generateMetaDescription(options.description),
    keywords,
    canonical,
    ogType: options.type || 'website',
    ogImage: options.image?.startsWith('http')
      ? options.image
      : options.image
        ? `${SITE_CONFIG.url}${options.image}`
        : `${SITE_CONFIG.url}${SITE_CONFIG.defaultImage}`,
    ogImageAlt: options.imageAlt,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    twitterCard: options.type === 'article' ? 'summary_large_image' : 'summary',
    noindex: options.noindex,
    nofollow: options.nofollow,
  }
}

/**
 * Apply meta tags to document head
 * Use this in route components to set meta tags dynamically
 */
export function applyMetaTags(meta: MetaTags): void {
  if (typeof document === 'undefined') return

  // Update title
  document.title = meta.title

  // Update or create meta tags
  const metaTags: Array<{ name?: string; property?: string; content: string }> = [
    { name: 'description', content: meta.description },
    ...(meta.keywords ? [{ name: 'keywords', content: meta.keywords }] : []),
    { property: 'og:title', content: meta.title },
    { property: 'og:description', content: meta.description },
    { property: 'og:type', content: meta.ogType || 'website' },
    ...(meta.ogImage ? [{ property: 'og:image', content: meta.ogImage }] : []),
    ...(meta.ogImageAlt ? [{ property: 'og:image:alt', content: meta.ogImageAlt }] : []),
    ...(meta.ogImageWidth ? [{ property: 'og:image:width', content: meta.ogImageWidth.toString() }] : []),
    ...(meta.ogImageHeight ? [{ property: 'og:image:height', content: meta.ogImageHeight.toString() }] : []),
    { name: 'twitter:card', content: meta.twitterCard || 'summary' },
    { name: 'twitter:site', content: SITE_CONFIG.twitterHandle },
    { name: 'twitter:title', content: meta.title },
    { name: 'twitter:description', content: meta.description },
    ...(meta.ogImage ? [{ name: 'twitter:image', content: meta.ogImage }] : []),
    ...(meta.canonical ? [{ property: 'og:url', content: meta.canonical }] : []),
    ...(meta.noindex ? [{ name: 'robots', content: 'noindex' }] : []),
    ...(meta.nofollow ? [{ name: 'robots', content: 'nofollow' }] : []),
  ]

  metaTags.forEach(tag => {
    const selector = tag.property
      ? `meta[property="${tag.property}"]`
      : `meta[name="${tag.name}"]`

    let element = document.querySelector(selector) as HTMLMetaElement | null

    if (!element) {
      element = document.createElement('meta')
      if (tag.property) {
        element.setAttribute('property', tag.property)
      } else if (tag.name) {
        element.setAttribute('name', tag.name)
      }
      document.head.appendChild(element)
    }

    element.setAttribute('content', tag.content)
  })

  // Update canonical link
  if (meta.canonical) {
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = meta.canonical
  }

  // Update robots meta tag
  const robotsValue = [meta.noindex ? 'noindex' : 'index', meta.nofollow ? 'nofollow' : 'follow']
    .filter(Boolean)
    .join(', ')

  if (robotsValue) {
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null
    if (!robots) {
      robots = document.createElement('meta')
      robots.name = 'robots'
      document.head.appendChild(robots)
    }
    robots.content = robotsValue
  }
}

/**
 * Predefined meta tags for common pages
 */
export const PREDEFINED_META = {
  home: {
    title: 'AIAlgo.trading - Master Algorithmic Trading',
    description: 'Learn algorithmic trading from industry experts. Comprehensive education, tools, and consulting services for automated trading strategies.',
    keywords: ['algorithmic trading', 'AI trading', 'automated trading', 'trading algorithms', 'algo trading'],
  },
  marketplace: {
    title: 'Algorithm Marketplace - Browse Trading Algorithms',
    description: 'Discover and download proven trading algorithms. Backtest results, performance metrics, and expert reviews.',
    keywords: ['trading algorithms', 'algorithm marketplace', 'download algorithms', 'trading bots'],
  },
  content: {
    title: 'Educational Content - Learn Algorithmic Trading',
    description: 'Free tutorials, guides, and courses on algorithmic trading. From beginner to advanced strategies.',
    keywords: ['trading tutorials', 'algorithmic trading course', 'trading education', 'learn trading'],
  },
  services: {
    title: 'Professional Services - Consulting & Development',
    description: 'Expert consulting, custom algorithm development, and trading system integration services.',
    keywords: ['trading consulting', 'algorithm development', 'trading services', 'custom algorithms'],
  },
} as const
